// Supported display currencies. GBP is the only currency bids/payments ever
// use — everything else here is presentation-only.
export const CURRENCIES = ["GBP", "HKD", "JPY", "USD", "EUR"] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];

export const DEFAULT_CURRENCY: CurrencyCode = "GBP";

export const CURRENCY_SYMBOL: Record<CurrencyCode, string> = {
  GBP: "£",
  HKD: "HK$",
  JPY: "¥",
  USD: "$",
  EUR: "€",
};

/** Currencies that don't use decimal subunits in everyday display. */
export const ZERO_DECIMAL_CURRENCIES: ReadonlySet<CurrencyCode> = new Set(["JPY"]);

export const CURRENCY_COOKIE = "bn_currency";

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (CURRENCIES as readonly string[]).includes(value);
}

/** Hardcoded fallback rates (GBP -> currency), used if the live fetch fails. */
export const FALLBACK_RATES: Record<CurrencyCode, number> = {
  GBP: 1,
  HKD: 9.85,
  JPY: 190,
  USD: 1.27,
  EUR: 1.17,
};

/** Best-effort country -> currency mapping used for auto-detection. */
const COUNTRY_CURRENCY: Record<string, CurrencyCode> = {
  GB: "GBP", IE: "EUR",
  HK: "HKD", MO: "HKD",
  JP: "JPY",
  US: "USD",
  DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", PT: "EUR",
  BE: "EUR", AT: "EUR", FI: "EUR", GR: "EUR", IE_: "EUR",
};

/** Maps an i18n locale to a sensible default currency. */
export function currencyForLocale(locale: string): CurrencyCode {
  if (locale === "zh-HK") return "HKD";
  if (locale === "ja-JP") return "JPY";
  return "GBP";
}

/** Best-effort detection from the resolved Intl timezone / country hints. */
export function currencyFromTimeZone(timeZone: string | undefined): CurrencyCode | null {
  if (!timeZone) return null;
  if (timeZone === "Asia/Hong_Kong") return "HKD";
  if (timeZone === "Asia/Tokyo") return "JPY";
  if (timeZone.startsWith("America/")) return "USD";
  if (timeZone === "Europe/London") return "GBP";
  if (timeZone.startsWith("Europe/")) return "EUR";
  return null;
}

export function currencyForCountry(country: string | undefined): CurrencyCode | null {
  if (!country) return null;
  return COUNTRY_CURRENCY[country.toUpperCase()] ?? null;
}
